import type { NetBlockDto } from '@terra/shared-contracts-external-network';

type NetblockWhois = NonNullable<NetBlockDto['whois']>;
type NetblockWhoisEntity = NetblockWhois['entities'][number];

const getWhoisEntities = ({
  whois,
}: {
  whois: NetblockWhois | null;
}): NetblockWhoisEntity[] => {
  if (!whois || !Array.isArray(whois.entities)) {
    return [];
  }

  return whois.entities;
};

const findEntityByRole = ({
  whois,
  role,
}: {
  whois: NetblockWhois | null;
  role: string;
}): NetblockWhoisEntity | null => {
  return (
    getWhoisEntities({ whois }).find(
      (entity) => Array.isArray(entity.roles) && entity.roles.includes(role),
    ) ?? null
  );
};

const toPreferredLabel = ({
  entity,
}: {
  entity: NetblockWhoisEntity | null;
}): string | null => {
  if (!entity) {
    return null;
  }

  return (
    entity.organization ?? entity.name ?? entity.email ?? entity.handle ?? null
  );
};

export const getNetblockWhoisOrganization = ({
  whois,
}: {
  whois: NetblockWhois | null;
}): string | null => {
  if (!whois) {
    return null;
  }

  const registrant = toPreferredLabel({
    entity: findEntityByRole({ whois, role: 'registrant' }),
  });

  if (registrant) {
    return registrant;
  }

  const administrative = toPreferredLabel({
    entity: findEntityByRole({ whois, role: 'administrative' }),
  });

  if (administrative) {
    return administrative;
  }

  const technical = toPreferredLabel({
    entity: findEntityByRole({ whois, role: 'technical' }),
  });

  if (technical) {
    return technical;
  }

  return whois.organization ?? whois.name ?? whois.netname ?? null;
};

export const getNetblockWhoisCountry = ({
  whois,
}: {
  whois: NetblockWhois | null;
}): string | null => {
  if (!whois) {
    return null;
  }

  if (whois.country) {
    return whois.country;
  }

  const registrant = findEntityByRole({ whois, role: 'registrant' });

  if (registrant?.country) {
    return registrant.country;
  }

  const administrative = findEntityByRole({ whois, role: 'administrative' });

  if (administrative?.country) {
    return administrative.country;
  }

  return findEntityByRole({ whois, role: 'technical' })?.country ?? null;
};
